/* prettier-ignore */

import { inject, Injectable, NgZone } from '@angular/core';
import { ScrollService } from './scrollContainer.service';
import { DataChatService } from './dataChat.service';
import { Subject } from 'rxjs';
import { AssistantToolsHandlerService } from './assistantToolsHandler.service';
import { v4 as uuidv4 } from 'uuid';

type TypingState = {
  fullText: string;
  index: number;
  timerId?: number;
  // HRS-3045: 'batch' = camino de texto SSE (micro-lotes, sin retraso artificial);
  // 'legacy' = por-carácter con delays (voz/WS, ritmo acoplado a la locución TTS).
  mode: 'batch' | 'legacy';
  // true cuando el stream señaló su fin (final/done/poll/fail). En modo batch el
  // completado exige esta señal: el buffer se vacía entre tokens constantemente.
  ended?: boolean;
};

@Injectable({ providedIn: 'root' })

export class ChatBotTextAnimationService {
  private ngZone = inject(NgZone);
  private dataChat = inject(DataChatService);
  private scrollService = inject(ScrollService);
  private assistantToolsHandlerService = inject(AssistantToolsHandlerService);

  private hiddenCommandsMap = new Map<string, string>();
  private typing = new Map<string, TypingState>();

  // Delays SOLO del modo legacy (voz/WS): el tipeo va de facto sincronizado con la
  // locución TTS, así que ese camino conserva el ritmo por-carácter.
  private baseDelay = 36;
  private punctuationDelay = 160;
  private charsPerTick = 2;
  private punctuationRegex = /[.,;:!?…]/;

  // HRS-3045: intervalo del micro-batching del camino de texto (rango válido 50-100 ms).
  // Cada lote vuelca TODO el pendiente en una única actualización + una pasada de CD,
  // en vez de 2 chars/36 ms (que añadía ~15 s a una respuesta de 600 caracteres).
  private flushIntervalMs = 80;

  private completedMessages = new Set<string>();
  private pendingProactive = new Map<string, { text: string; hiddenCommand?: string }>();
  private cdScheduled = false;

  // HRS-3045: emite el id de cada mensaje cuyo render quedó completado (texto
  // definitivo escrito en el signal). Lo consume la medición (render_done).
  // Sustituye al observable muerto allIdle$ (sin suscriptores ni emisiones).
  private messageRenderedSubject = new Subject<string>();
  public messageRendered$ = this.messageRenderedSubject.asObservable();

  constructor() {
    // HRS-2424: while the tab is hidden, requestAnimationFrame is paused and
    // setTimeout is throttled, so the char-by-char animation looks frozen even
    // though the WebSocket keeps delivering tokens. When the user comes back we
    // reveal everything received so far at once and flush change detection, so
    // the answer never appears to have "stopped" while away.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden)
          this.revealAllPendingTyping();
      });
    }
  }

  /** Reveals every in-progress message in full and finalizes it. */
  private revealAllPendingTyping() {
    for (const [id, state] of this.typing) {
      // HRS-3045: en modo batch el texto pendiente se vuelca, pero el completado
      // sigue exigiendo la señal de fin (markStreamEnded/setFinalContent); si el
      // stream terminó mientras la tab estaba oculta, ended ya está a true y
      // tickBatch completa aquí mismo.
      if (state.mode === 'batch') {
        this.tickBatch(id);
        continue;
      }

      if (state.timerId != null)
        clearTimeout(state.timerId);

      this.setAssistantMessageContent(id, state.fullText);
      this.assistantToolsHandlerService.handleTextUrls(id, state.fullText);
      this.completedMessages.add(id);
      this.typing.delete(id);
      this.flushPendingProactives(id);
    }

    // HRS-2424: the stream most likely finished while the tab was hidden (we keep
    // the typing state alive in the background, so it never turned the loader off).
    if (!this.dataChat.isPlaying())
      this.dataChat.isStreaming.set(false);

    this.ngZone.run(() => {});
    this.scrollService.scrollToBottom();
  }

  enqueueText(id: string, text: string, hiddenCommand?: string, opts?: { batch?: boolean }) {
    if (!text)
      return;

    const safeText = this.assistantToolsHandlerService.processAssistantTools(id, text);
    if (!safeText)
      return;

    this.dataChat.chatIsThinking.set(false);
    if (hiddenCommand)
      this.hiddenCommandsMap.set(id, hiddenCommand);

    const isProactive = id.includes('_tip');
    if (isProactive) {
      const parentId = this.getParentIdFromTip(id);
      if (!this.completedMessages.has(parentId)) {
        const pending = this.pendingProactive.get(id);
        if (pending) {
          pending.text += safeText;
          if (hiddenCommand) pending.hiddenCommand = hiddenCommand;
        } else {
          this.pendingProactive.set(id, { text: safeText, hiddenCommand });
        }
        return;
      }
    }

    const running = this.typing.get(id);
    if (running) {
      running.fullText += safeText;
      // HRS-3045: en modo batch el volcado va por lotes; si no hay flush programado,
      // se agenda uno (throttle a flushIntervalMs). El primer chunk ya se pintó
      // inmediatamente en startTyping.
      if (running.mode === 'batch')
        this.requestFlush(id);
      return;
    }

    this.startTyping(id, safeText, opts?.batch === true);
  }

  private startTyping(id: string, fullText: string, batch = false) {
    this.cancel(id, false);
    // Voz en reproducción: aunque el caller pida batch, se conserva el tipeo
    // acoplado a la locución (modo fijado por mensaje; no cambia a mitad).
    const mode: TypingState['mode'] = batch && !this.dataChat.isPlaying() ? 'batch' : 'legacy';
    this.typing.set(id, { fullText, index: 0, mode });

    const isProactive = id.includes('_tip');
    if (!isProactive)
      this.ensureMessageExists(id);

    if (mode === 'batch')
      this.tickBatch(id); // primer lote inmediato: el mensaje aparece cuanto antes
    else
      this.tick(id);
  }

  /**
   * HRS-3045: señal explícita de fin de stream para el modo batch (final/done/poll
   * completado/fail desde AskStreamingService). Vuelca lo pendiente y ejecuta los
   * efectos de completado. En modo legacy no aplica: ese camino completa al drenar,
   * como siempre (voz/WS no llaman aquí).
   */
  markStreamEnded(id: string) {
    const state = this.typing.get(id);
    if (!state || state.mode !== 'batch')
      return;

    state.ended = true;
    this.tickBatch(id);
  }

  /** Agenda un flush del lote si no hay ya uno pendiente (throttle). */
  private requestFlush(id: string) {
    const state = this.typing.get(id);
    if (!state || state.timerId != null)
      return;

    // Fuera de la zona: la única pasada de CD del lote la agenda scheduleChangeDetection.
    this.ngZone.runOutsideAngular(() => {
      state.timerId = window.setTimeout(() => {
        state.timerId = undefined;
        this.tickBatch(id);
      }, this.flushIntervalMs);
    });
  }

  /**
   * Volcado por lotes (HRS-3045): escribe TODO el texto pendiente en una única
   * actualización del signal + una sola pasada de change detection. El completado
   * solo corre con el buffer drenado Y la señal de fin (state.ended); un buffer
   * momentáneamente vacío entre tokens deja el estado vivo a la espera.
   */
  private tickBatch(id: string) {
    const state = this.typing.get(id);
    if (!state)
      return;

    if (state.timerId != null) {
      clearTimeout(state.timerId);
      state.timerId = undefined;
    }

    if (state.index < state.fullText.length) {
      const chunkToAppend = state.fullText.slice(state.index);
      state.index = state.fullText.length;
      this.handleAssistenQueueMessage(id, chunkToAppend);
      this.scheduleChangeDetection();
    }

    if (state.ended)
      this.completeMessage(id);
  }

  /**
   * Efectos de completado de un mensaje (compartidos por ambos modos): apagar el
   * loader si no hay audio, asociar URLs (gate `finished` de los link-previews),
   * liberar proactive tips y notificar el render completado (render_done).
   */
  private completeMessage(id: string) {
    const state = this.typing.get(id);
    if (!state)
      return;

    if (state.timerId != null)
      clearTimeout(state.timerId);

    if (!this.dataChat.isPlaying()) {
      this.dataChat.isStreaming.set(false);
      // this.dataChat.chatIsThinking.set(false);
    }
    this.assistantToolsHandlerService.handleTextUrls(id, state.fullText);
    this.typing.delete(id);
    this.completedMessages.add(id);
    this.scheduleChangeDetection();
    this.flushPendingProactives(id);
    this.messageRenderedSubject.next(id);
  }

  cancel(id: string, revealFullText = true) {
    const state = this.typing.get(id);
    if (!state) 
      return;

    if (state.timerId != null) 
      clearTimeout(state.timerId);

    if (revealFullText) {
      this.setAssistantMessageContent(id, state.fullText);
      this.assistantToolsHandlerService.handleTextUrls(id, state.fullText);
      this.scrollService.scrollToBottom()
    }

    this.typing.delete(id);
  }

  private tick(id: string) {
    const state = this.typing.get(id);
    if (!state)
      return;

    // HRS-2424: in a background tab requestAnimationFrame is paused and
    // setTimeout is throttled, so the char-by-char animation cannot keep up with
    // the WebSocket. Render the full buffered text at once and KEEP the typing
    // state alive (slow re-poll) instead of completing here: the normal path only
    // appends the per-tick slice, and the completion branch below deletes the
    // state without writing the skipped text — so fast-forwarding into it froze
    // the answer where the user left and discarded the entry before they came
    // back. The message is finalized on return via revealAllPendingTyping().
    if (document.hidden) {
      if (state.index < state.fullText.length) {
        state.index = state.fullText.length;
        this.setAssistantMessageContent(id, state.fullText);
        this.assistantToolsHandlerService.handleTextUrls(id, state.fullText);
        this.scheduleChangeDetection();
      }
      this.ngZone.runOutsideAngular(() => {
        state.timerId = window.setTimeout(() => this.tick(id), 250);
      });
      return;
    }

    if (state.index >= state.fullText.length) {
      // Modo legacy (voz/WS): completa al drenar, como siempre.
      this.completeMessage(id);
      return;
    }

    const prevIndex = state.index;
    const nextIndex = Math.min(state.index + this.charsPerTick, state.fullText.length);
    state.index = nextIndex;

    const chunkToAppend = state.fullText.slice(prevIndex, nextIndex);
    if (chunkToAppend)
      this.handleAssistenQueueMessage(id, chunkToAppend);

    const lastChar = state.fullText.slice(0, state.index).trimEnd().slice(-1);
    const delay = this.punctuationRegex.test(lastChar)
      ? this.punctuationDelay
      : this.baseDelay;

    this.scheduleChangeDetection();
    this.ngZone.runOutsideAngular(() => {
      state.timerId = window.setTimeout(() => this.tick(id), delay);
    });
  }

  private scheduleChangeDetection(): void {
    if (this.cdScheduled) return;
    this.cdScheduled = true;

    const flush = () => {
      this.cdScheduled = false;
      this.ngZone.run(() => {});
      this.scrollService.scrollToBottom()
    };

    this.ngZone.runOutsideAngular(() => {
      // HRS-2424: requestAnimationFrame is paused while the tab is hidden, which
      // would leave change detection (and the rendered text) frozen. Fall back
      // to a timer so the view keeps updating in the background.
      if (document.hidden)
        setTimeout(flush, 0);
      else
        requestAnimationFrame(flush);
    });
  }

  private flushPendingProactives(parentId: string) {
    for (const [tipId, pending] of this.pendingProactive.entries()) {
      if (this.getParentIdFromTip(tipId) === parentId) {
        this.pendingProactive.delete(tipId);

        if (pending.hiddenCommand) 
          this.hiddenCommandsMap.set(tipId, pending.hiddenCommand);

        this.startTyping(tipId, pending.text);
      }
    }
  }

  private getParentIdFromTip(id: string): string {
    return id?.split('_tip')?.[0];
  }

  private ensureMessageExists(id: string) {
    this.dataChat.messages.update((prev) => {
      const exists = prev.some((m: any) => m.id === id);
      if (exists) 
        return prev;

      return [
        ...prev,
        {
          id,
          role: 'assistant',
          content: '',
          show: true,
        },
      ];
    });
  }

  /**
   * Reconciliación del stream v3: sustituye el contenido del mensaje por el
   * texto definitivo ya post-procesado del backend (evento `final`). Cancela
   * cualquier animación en curso para ese id y deja el texto limpio (sin tags
   * PROACTIVE/ACTION crudos). Idempotente y seguro tanto si hubo streaming de
   * tokens como si no (en cuyo caso simplemente pinta el final).
   */
  setFinalContent(id: string, text: string) {
    // cancelar la animación en curso SIN revelar el texto crudo acumulado
    // (también descarta cualquier flush de lote pendiente, HRS-3045)
    this.cancel(id, false);
    // El texto de `final` es completo: un [ACTION: a medias que dejara el último token
    // no debe anteponerse a él al parsear.
    this.assistantToolsHandlerService.resetPartialActionBuffer(id);
    const safeText = this.assistantToolsHandlerService.processAssistantTools(id, text);
    this.ensureMessageExists(id);
    this.setAssistantMessageContent(id, safeText ?? '');
    this.assistantToolsHandlerService.handleTextUrls(id, safeText ?? '');
    this.completedMessages.add(id);
    this.scheduleChangeDetection();
    this.flushPendingProactives(id);
    // render_done para la medición: el texto definitivo ya está en el signal
    this.messageRenderedSubject.next(id);
  }

  private setAssistantMessageContent(id: string, content: string) {
    this.dataChat.messages.update((prev) => {
      const index = prev.findIndex((m: any) => m.id === id);
      if (index === -1) 
        return prev;

      const updated = [...prev];
      updated[index] = { ...updated[index], content };
      return updated;
    });
  }

  createUserMessage(message: string) {
    if (!message || !message.trim()) 
      return;

    this.dataChat.messages.update((prev) => {
      // Evitar duplicados exactos si se llama dos veces seguidas (ej: voz + websocket)
      const lastMsg = prev.length > 0 ? prev[prev.length - 1] : null;
      if (lastMsg && lastMsg.role === 'user' && lastMsg.content === message) {
        return prev;
      }
      // id local: el @for del bubble trackea por message.id (HRS-3045, evita claves undefined)
      return [...prev, { id: uuidv4(), role: 'user', content: message, show: true }];
    });

    this.scrollService.scrollToBottom(true)
  }

  handleAssistenQueueMessage(id: string, text: string) {
    this.dataChat.messages.update((prev) => {
      const index = prev.findIndex((m: any) => m.id === id);
      const isProactive = id.includes('_tip');
      const hiddenCommand = this.hiddenCommandsMap.get(id);
      if (hiddenCommand) 
        this.hiddenCommandsMap.delete(id);

      if (index !== -1) {
        // Mensaje ya existe → actualizar contenido (chunk append)
        const updated = [...prev];
        const target = updated[index];

        if (isProactive) {
          // Actualizar el proactiveChildren del padre
          const parentIdx = this._findParentBotIndex(updated, index);
          if (parentIdx !== -1) {
            const parent = updated[parentIdx];
            const children = [...(parent.proactiveChildren || [])];
            const childIdx = children.findIndex(c => c.id === id);
            if (childIdx !== -1) {
              children[childIdx] = {
                ...children[childIdx],
                content: (children[childIdx].content ?? '') + text,
                hiddenCommand: hiddenCommand || children[childIdx].hiddenCommand
              };
            }
            updated[parentIdx] = { ...parent, proactiveChildren: children };
          }
        }

        updated[index] = {
          ...target,
          content: (target.content ?? '') + text,
          isProactive,
          hiddenCommand: hiddenCommand || target.hiddenCommand
        };
        return updated;
      }

      // Mensaje nuevo
      const newMsg: any = { 
        role: 'assistant', 
        content: text, 
        show: true, 
        id, 
        isProactive, hiddenCommand 
      };

      if (isProactive) {
        // Adjuntar al último mensaje bot como proactiveChildren[] y ocultar el standalone
        const updated = [...prev];
        const parentIdx = this._findLastBotIndex(updated);
        if (parentIdx !== -1) {
          const parent = updated[parentIdx];
          const children = [...(parent.proactiveChildren || [])];
          children.push({ ...newMsg });
          updated[parentIdx] = { ...parent, proactiveChildren: children };
          // Añadir al array con show:false (oculto, solo para tracking de chunks)
          return [...updated, { ...newMsg, show: false }];
        }
      }

      return [...prev, newMsg];
    });
  }

  private _findParentBotIndex(messages: any[], proactiveIdx: number): number {
    for (let i = proactiveIdx - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && !messages[i].isProactive) return i;
    }
    return -1;
  }

  private _findLastBotIndex(messages: any[]): number {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && !messages[i].isProactive) return i;
    }
    return -1;
  }

}